import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Button,
  IconButton,
  Menu,
  Tooltip,
  LinearProgress,
  MenuItem, TablePagination, TableFooter
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import Emitter from '@utils/eventEmitter'
import {formatUnderFromCamel} from '@utils/formatText'
import HistoryModal from '@views/adminEmployee/modal/HistoryModal'
import Modal from '@views/adminEmployee/modal/FormModal'
import DynamicHead from '@components/DynamicHead'
import DynamicSearch from '@components/DynamicSearch'

import {
  adminGetMemberList,
  adminCheckMember,
  adminAddMember,
  adminUpdateMember,
  adminDeleteMember,
  adminDeleteSelectedMember,
  adminGetMemberWithExcel,
  adminDownloadMemberAttach,
} from '@store/admin/member'

const ItemTableRow = styled(TableRow)({
  cursor: 'pointer',
  transition: '.3s background-color',
  '&.ing': {
    backgroundColor: '#d1e8ff',
    opacity: .8,
  },
  '&.new': {
    backgroundColor: '#ffe7e7',
    opacity: .8,
  }
})
const ItemTableCell = styled(TableCell)({
  cursor: 'pointer',
  transition: '.3s opacity',
  '&.ing': {
    backgroundColor: '#d1e8ff',
    opacity: .8,
  },
  '&.new': {
    backgroundColor: '#ffe7e7',
    opacity: .8,
  },
  '&:hover': {
    opacity: 1,
  },
})
const ButtonGroup = styled(Stack)({
  padding: '10px 0',
  textAlign: 'right'
})
const OptionsButton = styled(IconButton)({
  padding: 0
})

const ExcelButton = styled(Button)({
  position: 'absolute',
  left: 20
})

const headerList = [
  {key: '', name: '', sort: false, checkbox: true},
  {key: 'name', name: '성명'},
  {key: 'birth', name: '생년월일', sort: true, search: true},
  {key: 'companyName', name: '소속회사', sort: true, search: true},
  {key: 'position', name: '직책', sort: false},
  {key: 'signExpectDate', name: '선임(예정)일', sort: true, search: true},
  {key: 'resignExpectDate', name: '사임(예정)일', sort: true, search: true},
  {key: 'personalInfoAgreeName', name: '개인정보 동의서', sort: true, search: true},
  {key: 'noloanAgreeName', name: '대출 불가 확약서', sort: true, search: true},
  {key: 'corporateRegistrationName', name: '법인등기부등본', sort: true, search: true},
  {key: '', name: ''},
]

const initParams = {
  page: 0,
  rowsPerPage: 10,
  searchType: 'COMPANY_NAME',
  searchKeyword: '',
  sortDirection: null
}

const EmployeeTable = ({
  groupId,
  groupName,
}) => {
  const dispatch = useDispatch()
  const {
    adminMemberList,
    adminMemberListTotalSize,
  } = useSelector(store => store.memberAdmin)
  const [ checkeds, setCheckeds ] = useState({})
  const [ modalOpen, setModalOpen ] = useState(false)
  const [ historyModalOpen, setHistoryModalOpen ] = useState(false)
  const [ editData, setEditData ] = useState(null)
  const [ historyData, setHistoryData ] = useState(null)
  const [ params, setParams ] = useState(initParams)
  const [ loading, setLoading ] = useState(false)

  const handleChangePage = (e, newPage) => {
    setParams(Object.assign({}, params, {page: newPage}))
  }

  const handleChangeRowsPerPage = (e) => {
    const currentPageSize = parseInt(e.target.value, 10)
    setParams(Object.assign({}, params, {page: 0, rowsPerPage: currentPageSize}))
  }

  const onSelectAllClick = () => {
    let updateCheckeds = {}
    if (!isAllChecked) {
      adminMemberList.forEach(data => {
        updateCheckeds[data.companyMemberCode] = true
      })
    }
    setCheckeds(updateCheckeds)
  }

  const changeChecked = (itemId) => {
    let updateCheckeds = Object.assign({}, checkeds)
    if (checkeds[itemId]) {
      updateCheckeds = Object.assign({}, checkeds)
      delete updateCheckeds[itemId]
    } else {
      updateCheckeds[itemId] = true
    }
    setCheckeds(updateCheckeds)
  }

  const isAllChecked = useMemo(() => {
    if (!adminMemberList.length) return false;
    return Object.keys(checkeds).length === adminMemberList.length
  }, [ checkeds, adminMemberList ])

  const completeModal = async (action, data, companyMemberCode) => {
    setModalOpen(false)
    if (action) {
      if (companyMemberCode) {
        dispatch(adminUpdateMember({
          form: data,
          companyMemberCode
        }))
      } else {
        await dispatch(adminAddMember({form: data}))
        onGetMemberList()
      }
    }
  }

  const openEditModal = (member, modalType) => {
    if (modalType === 'new') {
      setEditData(null)
      setModalOpen(true)
      return
    }
    if (member) {
      setEditData(member)
      setModalOpen(true)
      return
    }
    const checkedList = Object.keys(checkeds)

    if (checkedList.length === 0) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '수정 할 임원을 선택하세요.',
        color: 'warning'
      })
    } else if (checkedList.length > 1) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '한명의 임원을 선택하세요.',
        color: 'warning'
      })
    } else {
      setEditData(adminMemberList.find(member => member.companyMemberCode === checkedList[0]))
      setModalOpen(true)
    }
  }

  const openDeleteModal = (data) => {
    Emitter.emit('SHOW_CONFIRM', {
      text: '삭제하시겠습니까?',
      successCb: () => {
        dispatch(adminDeleteMember({
          companyMemberCode: data.companyMemberCode,
        }))
      }
    })
  }

  const openSelectedDeleteModal = () => {
    const checkedLength = Object.keys(checkeds).length
    if (!checkedLength) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '선택된 데이터가 없습니다.',
        color: 'error'
      })
      return
    }
    Emitter.emit('SHOW_CONFIRM', {
      text: `${checkedLength}개의 데이터를 삭제하시겠습니까?`,
      successCb: () => {
        const checkedList = Object.keys(checkeds)

        dispatch(adminDeleteSelectedMember({
          selecteds: checkedList
        }))
      }
    })
  }

  const openHistoryModal = (companyMemberCode) => {
    setHistoryData(companyMemberCode)
    setHistoryModalOpen(true)
  }

  const downloadExcels = () => {
    dispatch(adminGetMemberWithExcel({
      groupId,
      groupName
    }))
  }

  const downloadFile = (path, name, keyId) => {
    dispatch(adminDownloadMemberAttach({
      filePath: path,
      fileName: name,
      keyId: keyId,
    }))
  }

  const changeDirection = (key, value) => {
    const tempDirection = {}
    tempDirection[key] = value
    setParams(Object.assign({}, params, {
      page: 0,
      sortDirection: tempDirection
    }))
  }

  const onClearSearchInputEl = useCallback(() => {
    const keywordInput = document.querySelector('.keyword input')
    if (keywordInput) keywordInput.value = ''
  }, [])

  const onChangeSearchType = (value) => {
    onClearSearchInputEl()
    let newParams = {
      page: 0,
      searchType: value,
      searchKeyword: ''
    }
    setParams(Object.assign({}, params, newParams))
  }

  const onChangeSearchKeyword = (value) => {
    setParams(Object.assign({}, params, {
      page: 0,
      searchKeyword: value
    }))
  }

  const getFilterSort = useCallback(() => {
    let sort = []
    const {sortDirection} = params;
    if (sortDirection) {
      sort = Object.keys(sortDirection)
        .filter(key => sortDirection[key])
        .map(key => {
          const sortType = formatUnderFromCamel({text: key});
          return `${sortType},${sortDirection[key]}`
        })
    }
    return sort.length ? sort : undefined
  }, [params])

  const onGetMemberList = useCallback(async () => {
    if (!params.groupId) return
    const {page, rowsPerPage, groupId, searchType, searchKeyword} = params
    const resultParams = {
      groupId,
      page,
      size: rowsPerPage,
      searchType: searchKeyword ? searchType : undefined,
      searchKeyword: searchKeyword || undefined,
      sort: getFilterSort(),
    }
    setLoading(true)
    await dispatch(adminGetMemberList(resultParams))
    setLoading(false)
  }, [dispatch, params, getFilterSort])

  useEffect(() => onGetMemberList(), [onGetMemberList])

  useEffect(() => {
    setParams(Object.assign({}, initParams, {groupId}))
    setCheckeds({})
    onClearSearchInputEl()
  }, [groupId, onClearSearchInputEl])

  return (
    <>
      <DynamicSearch 
        list={headerList}
        searchType={params.searchType}
        searchKeyword={params.searchKeyword}
        onChangeSearchKeyword={onChangeSearchKeyword}
        onChangeSearchType={onChangeSearchType}
      />
      <TableContainer component={Paper} sx={{position: 'relative'}}>
        {loading && <LinearProgress sx={{position: 'absolute', width: '100%'}}/>}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <DynamicHead 
            list={headerList}
            sortDirection={params.sortDirection}
            changeDirection={changeDirection}
            isAllChecked={isAllChecked}
            onSelectAllClick={onSelectAllClick}
          />
          <TableBody>
            {
              adminMemberList && adminMemberList.map(item => (
                <RoleTableItem 
                  item={item} 
                  key={item.companyMemberCode}
                  checked={checkeds[item.companyMemberCode]}
                  changeChecked={changeChecked}
                  openEditModal={openEditModal}
                  openDeleteModal={openDeleteModal}
                  openHistoryModal={openHistoryModal}
                  filteredCheckList={item.memberCheckList?.filter(memberCheck => !memberCheck.check)}
                  downloadFile={downloadFile}
                  onGetMemberList={onGetMemberList}
                />
              ))
            }
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={adminMemberListTotalSize}
                page={params.page}
                rowsPerPage={params.rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 30, 50]}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <ButtonGroup 
        direction="row"
        justifyContent="flex-end"
        spacing={1}
      >
        <ExcelButton variant="contained" disableElevation onClick={downloadExcels}>엑셀 다운로드</ExcelButton>
        <Button variant="contained" disableElevation onClick={() => openEditModal(null, 'new')}>등록</Button>
        <Button variant="contained" disableElevation onClick={() => openEditModal(null, 'edit')}>수정</Button>
        <Button variant="outlined" onClick={openSelectedDeleteModal}>삭제</Button>
      </ButtonGroup>

      {modalOpen && <Modal 
        data={editData}
        open={modalOpen}
        completeModal={completeModal}
      />}

      {historyModalOpen && <HistoryModal
        data={historyData}
        open={historyModalOpen}
        closeModal={() => setHistoryModalOpen(false)}
      />}
    </>
  )
}

const RoleTableItem = ({
  item,
  checked,
  changeChecked,
  openEditModal,
  openDeleteModal,
  openHistoryModal,
  filteredCheckList,
  downloadFile,
  onGetMemberList,
}) => {
  const dispatch = useDispatch()
  const [ anchorEl, setAnchorEl ] = useState(null)
  const open = !!anchorEl

  const handleShowItems = (e) => {
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseItems = () => {
    setAnchorEl(null)
  }

  const handleOpenEditModal = () => {
    setAnchorEl(null)

    openEditModal(item)
  }

  const handleOpenHistoryModal = () => {
    openHistoryModal(item.companyMemberCode)
  }

  const handleOpenDeleteConfirm = () => {
    setAnchorEl(null)
    openDeleteModal(item)
  }

  const checkMemberDiff = async (checkList, type, event) => {
    if (!checkList) {
      return
    }

    const tempCheckList = checkList.filter(check => check.type === type)

    if (tempCheckList.length > 0) {
      await dispatch(adminCheckMember({
        checkList: tempCheckList
      }))
      onGetMemberList()
    }
  }

  const target = useMemo(() => {
    return filteredCheckList.find(check => check.type === 'NEW');
  }, [filteredCheckList]);

  const isNew = useMemo(() => {
    if (!target) return false;
    const {checkUser} = target;
    const checkUserLength = checkUser.length;
    return !checkUserLength;
  }, [target]);

  const isChecking = useMemo(() => {
    if (!target) return false;
    const {checkComplete, check} = target;
    return !checkComplete && !check;
  }, [target]);

  const classNames = useMemo(() => {
    if (isNew) {
      return 'new'
    } else if (isChecking) {
      return 'ing'
    }
    return '';
  }, [isNew, isChecking]);

  return (
    <ItemTableRow
      className={classNames}
      onClick={(e) => checkMemberDiff(filteredCheckList, 'NEW', e)}
    >
      <TableCell padding="checkbox" align="center">
        <Checkbox
          color="primary"
          checked={checked || false}
          onChange={() => changeChecked(item.companyMemberCode)}
        />
      </TableCell>
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'NAME'}
        checkMemberDiff={checkMemberDiff}
        text={item.name}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'BIRTH'}
        checkMemberDiff={checkMemberDiff}
        text={item.birth}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'COMPANY_NAME'}
        checkMemberDiff={checkMemberDiff}
        text={item.companyName}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'POSITION'}
        checkMemberDiff={checkMemberDiff}
        text={item.position}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'SIGN_EXPECT_DATE'}
        checkMemberDiff={checkMemberDiff}
        text={item.signExpectDate}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'RESIGN_EXPECT_DATE'}
        checkMemberDiff={checkMemberDiff}
        text={item.resignExpectDate}
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'PERSONAL_INFO_AGREE_NAME'}
        checkMemberDiff={checkMemberDiff}
        text={
          <DownloadItem 
            item={item.attachInfo}
            itemKey={'personalInfoAgree'}
            downloadFile={downloadFile}
          />
        }
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'NOLOAN_AGREE_NAME'}
        checkMemberDiff={checkMemberDiff}
        text={
          <DownloadItem 
            item={item.attachInfo}
            itemKey={'noloanAgree'}
            downloadFile={downloadFile}
          />
        }
      />
      <ClickCheckItemTableCell 
        filteredCheckList={filteredCheckList}
        checkListKey={'CORPORATE_REGISTRATION_NAME'}
        checkMemberDiff={checkMemberDiff}
        text={
          <DownloadItem 
            item={item}
            itemKey={'corporateRegistration'}
            downloadFile={downloadFile}
          />
        }
      />
      <ItemTableCell>
        <OptionsButton component="button" onClick={handleShowItems}>
          <MoreVertIcon />
        </OptionsButton>
        <Menu
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseItems}
        >
          <MenuItem sx={{minWidth: 100}} onClick={handleOpenHistoryModal}>
            이력
          </MenuItem>
          <MenuItem onClick={handleOpenEditModal}>
            변경
          </MenuItem>
          <MenuItem onClick={handleOpenDeleteConfirm}>
            삭제
          </MenuItem>
        </Menu>
      </ItemTableCell>
    </ItemTableRow>
  )
}

const ClickCheckItemTableCell = ({
  filteredCheckList,
  checkListKey,
  checkMemberDiff,
  text,
}) => {

  const target = useMemo(() => {
    return filteredCheckList.find(check => check.type === checkListKey);
  }, [filteredCheckList, checkListKey]);

  const isNew = useMemo(() => {
    if (!target) return false;
    const {checkUser} = target;
    const checkUserLength = checkUser.length;
    return !checkUserLength;
  }, [target]);

  const isChecking = useMemo(() => {
    if (!target) return false;
    const {checkComplete, check} = target;
    return !checkComplete && !check;
  }, [target]);

  const classNames = useMemo(() => {
    if (isNew) {
      return 'new'
    } else if (isChecking) {
      return 'ing'
    }
    return '';
  }, [isNew, isChecking]);

  return (
    <ItemTableCell 
      align="center" 
      className={classNames}
      onClick={(e) => checkMemberDiff(filteredCheckList, checkListKey, e)}
    >
      {text}
    </ItemTableCell>
  )
}

const DownloadItem = ({
  item,
  itemKey,
  downloadFile
}) => {
  if (!item) return <></>;
  
  const name = item[`${itemKey}Name`];
  const path = item[`${itemKey}Path`];
  const key = item[`${itemKey}Key`];

  if (!name) return <></>;

  return (
    <Tooltip title={name}>
      <Button
        onClick={() => downloadFile(path, name, key)}
        variant="outlined"
        className="attachFile-btn"
      >
        {name}
      </Button>
    </Tooltip>
  )
}

export default EmployeeTable